import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ReportDefinition, ReportPage, ReportRow } from './report.model';

/**
 * Fetches Bordereau report data and Excel downloads from the API.
 *
 * Response keys are lowercased so component bindings never depend on the API's
 * exact camelCase (System.Text.Json only lowercases the first character, e.g.
 * `XOLCost` → `xOLCost`).
 */
@Injectable({ providedIn: 'root' })
export class ReportDataService {
  /** apiBaseUrl without any trailing slash. */
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  constructor(private readonly http: HttpClient) {}

  /** Loads the report's JSON rows for the given (optional) reinsurance year. */
  fetchData(def: ReportDefinition, year?: number): Observable<ReportRow[]> {
    const url = this.buildUrl(def.dataPath, def.requiresYear ? year : undefined);
    const request =
      def.dataMethod === 'GET'
        ? this.http.get<unknown[]>(url)
        : this.http.post<unknown[]>(url, null);
    return request.pipe(map((rows) => (rows ?? []).map((row) => normaliseKeys(row))));
  }

  /**
   * Loads one page of a server-paged report (`def.paged`). `pageNumber` is
   * 1-based, matching the API's `PagedResponse`.
   */
  fetchPage(
    def: ReportDefinition,
    pageNumber: number,
    pageSize: number,
    year?: number
  ): Observable<ReportPage> {
    const url = this.buildUrl(def.dataPath, def.requiresYear ? year : undefined, {
      pageNumber,
      pageSize
    });
    const request =
      def.dataMethod === 'GET'
        ? this.http.get<PagedResponse>(url)
        : this.http.post<PagedResponse>(url, null);
    return request.pipe(
      map((response) => {
        const page = normaliseKeys(response);
        const rows = (page['data'] as unknown[] | undefined) ?? [];
        return {
          rows: rows.map((row) => normaliseKeys(row)),
          totalRecords: Number(page['totalrecords'] ?? rows.length)
        };
      })
    );
  }

  /**
   * Downloads the report's Excel file and triggers a browser save.
   * The server filename can't be read cross-origin (Content-Disposition isn't
   * exposed by CORS), so a stable fallback name is used.
   */
  downloadExcel(def: ReportDefinition, year?: number): Observable<void> {
    const url = this.buildUrl(def.downloadPath, def.requiresYear ? year : undefined);
    const fallback =
      def.requiresYear && year != null
        ? `${def.downloadFileBase}_${year}.xlsx`
        : `${def.downloadFileBase}.xlsx`;
    return this.http
      .get(url, { observe: 'response', responseType: 'blob' })
      .pipe(map((response) => saveBlob(response, fallback)));
  }

  private buildUrl(path: string, year?: number, extra: Record<string, number> = {}): string {
    const params = Object.entries({ ...(year != null ? { reinsuranceYear: year } : {}), ...extra });
    const query = params.length ? `?${params.map(([k, v]) => `${k}=${v}`).join('&')}` : '';
    return `${this.base}/${path}${query}`;
  }
}

/** Raw `PagedResponse<T>` envelope returned by server-paged endpoints. */
type PagedResponse = Record<string, unknown>;

/** Returns a copy of the object with every top-level key lowercased. */
function normaliseKeys(row: unknown): ReportRow {
  const out: ReportRow = {};
  if (row && typeof row === 'object') {
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      out[key.toLowerCase()] = value;
    }
  }
  return out;
}

/** Reads the filename from Content-Disposition when available, else the fallback. */
function filenameFrom(response: HttpResponse<Blob>, fallback: string): string {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

/** Triggers a browser download of the response body. */
function saveBlob(response: HttpResponse<Blob>, fallback: string): void {
  const blob = response.body;
  if (!blob) {
    return;
  }
  const filename = filenameFrom(response, fallback);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
